import { cva, type VariantProps } from 'class-variance-authority'
import { Button } from 'designSystem/components/Button'
import { Input } from 'designSystem/components/Input'
import { Textarea } from 'designSystem/components/Textarea'
import { cn } from 'designSystem/lib/utils'
import * as React from 'react'

const inputGroupVariants = cva(
  'group/input-group relative flex w-full items-center rounded-md border shadow-xs transition-[color,box-shadow] outline-none',
  {
    variants: {
      variant: {
        default: 'border-input bg-background dark:bg-input/30',
        surface: 'border-border bg-secondary',
        embedded: 'border-transparent bg-transparent shadow-none',
      },
      layout: {
        default: '',
        stacked: 'h-auto flex-col items-stretch',
      },
    },
    defaultVariants: {
      variant: 'default',
      layout: 'default',
    },
  }
)

function InputGroup({
  className,
  variant = 'default',
  layout = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof inputGroupVariants> & {
    /** Row height for the default layout: `sm` matches `lg` buttons and `sm` tabs. */
    size?: 'default' | 'sm'
  }): React.ReactElement {
  return (
    <div
      data-slot='input-group'
      data-variant={variant}
      data-layout={layout}
      role='group'
      className={cn(
        inputGroupVariants({ variant, layout }),
        // Fixed height only for the default row; stacked keeps CVA h-auto (twMerge
        // would otherwise drop bare h-auto when a height is appended unconditionally).
        layout === 'default' && size === 'sm' && 'h-8 has-[>textarea]:h-auto',
        layout === 'default' && size !== 'sm' && 'h-9 has-[>textarea]:h-auto',
        'min-w-0',
        'has-[>[data-align=inline-start]]:[&>input]:pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-2',
        'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',
        'has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
        'has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
        className
      )}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start':
          'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
        'inline-end':
          'order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]',
        'block-start':
          'order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3',
        'block-end':
          'order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
)

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role='group'
      data-slot='input-group-addon'
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return
        }
        e.currentTarget.parentElement
          ?.querySelector<
            HTMLInputElement | HTMLTextAreaElement
          >('input, textarea')
          ?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva(
  'flex items-center gap-2 text-sm shadow-none',
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        sm: 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
        'icon-xs':
          'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
        'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  }
)

// Thin structure only — product chrome (e.g. landing composer min-heights)
// stays feature-local via className.
const inputGroupSectionVariants = cva('flex w-full items-center', {
  variants: {
    placement: {
      control: 'gap-2 border-b border-border px-3',
      actions: 'justify-between gap-3 px-3 py-2',
    },
  },
  defaultVariants: {
    placement: 'control',
  },
})

function InputGroupSection({
  className,
  placement = 'control',
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof inputGroupSectionVariants>) {
  return (
    <div
      data-slot='input-group-section'
      data-placement={placement}
      className={cn(inputGroupSectionVariants({ placement }), className)}
      {...props}
    />
  )
}

function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<'input'>) {
  return (
    <Input
      data-slot='input-group-control'
      className={cn(
        'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent',
        className
      )}
      {...props}
    />
  )
}

const inputGroupTextareaVariants = cva(
  'flex-1 resize-none rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent',
  {
    variants: {
      // padding axis only — "compact" collided with size language elsewhere
      padding: {
        default: 'py-3',
        none: 'min-h-5 py-0',
      },
    },
    defaultVariants: {
      padding: 'default',
    },
  }
)

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'> &
    VariantProps<typeof inputGroupTextareaVariants>
>(({ className, padding = 'default', ...props }, ref) => (
  <Textarea
    ref={ref}
    data-slot='input-group-control'
    data-padding={padding}
    className={cn(inputGroupTextareaVariants({ padding }), className)}
    {...props}
  />
))
InputGroupTextarea.displayName = 'InputGroupTextarea'

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupSection,
  InputGroupText,
  InputGroupTextarea,
  inputGroupVariants,
}
